import type {ReactElement} from 'react';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import {AccountResizeStatus} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import type {PublishParamsPreviewCtaProps} from './publish-params-preview-cta.types';
import styles from './publish-params-preview-cta.module.scss';

function isRetryCta(accountResizeStatus: AccountResizeStatus): boolean {
  return accountResizeStatus === AccountResizeStatus.RESIZE_ERROR;
}

function isResizeCta(publishingAction: PublishParamsPreviewCtaProps['publishingAction'], doesAnyPosterNeedResize: boolean): boolean {
  return publishingAction === undefined && doesAnyPosterNeedResize;
}

function shouldHideResizeButton(accountType: SocialPlatform): boolean {
  switch (accountType) {
    case SocialPlatform.PINTEREST:
      return true;
    default:
      return false;
  }
}

function getResizeCta(onResize: () => void, isAnyPosterLoading: boolean): ReactElement {
  return (
    <Button
      key="resize-cta"
      icon="icon-resize"
      id="add-details-onboarding-resize-button"
      type={ButtonType.SECONDARY}
      size={ButtonSize.MEDIUM}
      text={window.i18next.t('pmwjs_resize_options')}
      onClick={onResize}
      customClasses={`${styles.previewCtaButton} ${isAnyPosterLoading ? styles.previewCtaButtonHiddenWhileLoading : ''}`}
    />
  );
}

function getCoverCta(onClickCover: () => void, coverCtaText: string, isAnyPosterLoading: boolean, isPreviewLoading: boolean): ReactElement {
  return (
    <div key="cover-cta" className="_position-relative">
      <Button
        icon="icon-image"
        type={ButtonType.SECONDARY}
        size={ButtonSize.MEDIUM}
        text={coverCtaText}
        onClick={onClickCover}
        customClasses={`${styles.previewCtaButton} ${isAnyPosterLoading || isPreviewLoading ? '_hidden' : ''}`}
      />
    </div>
  );
}

function getRetryCta(onRetry: () => void): ReactElement {
  return (
    <Button
      key="retry-cta"
      icon="icon-rotate-ccw"
      type={ButtonType.SECONDARY}
      size={ButtonSize.MEDIUM}
      text={window.i18next.t('pmwjs_retry')}
      onClick={onRetry}
      customClasses={styles.previewCtaButton}
    />
  );
}

export function PublishParamsPreviewCta({
  wizardStep,
  accountResizeStatus,
  doesAnyPosterNeedResize,
  publishingAction,
  accountType,
  isAnyPosterLoading,
  isPreviewLoading,
  isCoverCta,
  coverCtaText,
  onResize,
  onRetry,
  onClickCover,
}: PublishParamsPreviewCtaProps): ReactElement | null {
  if (wizardStep === SocialMediaWizardStep.PUBLISH) {
    return null;
  }

  if (isRetryCta(accountResizeStatus)) {
    return <div className={styles.previewCtaBar}>{getRetryCta(onRetry)}</div>;
  }

  const ctas: ReactElement[] = [];

  if (isResizeCta(publishingAction, doesAnyPosterNeedResize) && !shouldHideResizeButton(accountType)) {
    ctas.push(getResizeCta(onResize, isAnyPosterLoading));
  }

  if (isCoverCta) {
    ctas.push(getCoverCta(onClickCover, coverCtaText, isAnyPosterLoading, isPreviewLoading));
  }

  if (ctas.length === 0) {
    return null;
  }

  return <div className={styles.previewCtaBar}>{ctas}</div>;
}
